import {useCallback, useEffect, useState} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {
  getAnchorCity,
  getAnchorDisplayName,
  getSearchParametersBoundingBox
} from 'modules/sapiSearch/selectors'
import {
  getIsUserLocationSearch,
  getIsUserOrUrlLocationSearch
} from 'modules/search/selectors'
import {getSearchBoxDestinationDisplayValue} from 'modules/searchBox/selectors'

export const userLocationMessages = defineMessages({
  mapArea: {
    id: 'mapArea',
    defaultMessage: 'Map area'
  },
  userLocation: {
    id: 'nearMe',
    defaultMessage: 'Near me'
  }
})

export const useDestinationDisplayName = () => {
  const {formatMessage} = useIntl()
  const boundingBox = useSelector(getSearchParametersBoundingBox)
  const isUserLocationSearch = useSelector(getIsUserLocationSearch)
  const isUserOrUrlLocationSearch = useSelector(getIsUserOrUrlLocationSearch)

  const anchorDisplayName = useSelector(getAnchorDisplayName)
  const anchorCityName = useSelector(getAnchorCity)
  const searchBoxDestinationDisplayValue = useSelector(
    getSearchBoxDestinationDisplayValue
  )

  // Pre-format messages so the callback deps are value-stable strings
  // rather than the `formatMessage` function identity.
  const userLocationText = formatMessage(userLocationMessages.userLocation)
  const mapAreaText = formatMessage(userLocationMessages.mapArea)

  const getDestinationDisplayName = useCallback(() => {
    if (searchBoxDestinationDisplayValue) {
      return searchBoxDestinationDisplayValue
    }

    if (isUserLocationSearch) {
      return isUserOrUrlLocationSearch ? userLocationText : anchorCityName
    }

    if (boundingBox) {
      return mapAreaText
    }

    return anchorDisplayName || ''
  }, [
    anchorCityName,
    anchorDisplayName,
    boundingBox,
    isUserLocationSearch,
    isUserOrUrlLocationSearch,
    mapAreaText,
    searchBoxDestinationDisplayValue,
    userLocationText
  ])

  // Lazy init so SSR and the first client render get the computed value
  // synchronously. The effect below handles subsequent updates and ignores
  // transient falsy values from in-flight search mutations.
  const [destinationName, setDestinationName] = useState(
    getDestinationDisplayName
  )

  useEffect(() => {
    const newDisplayName = getDestinationDisplayName()
    setDestinationName(prev =>
      newDisplayName !== prev && newDisplayName ? newDisplayName : prev
    )
  }, [getDestinationDisplayName])

  return destinationName
}
